import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import Layout from "./components/Layout";
import LoginPage from "./pages/Login";
import OverviewPage from "./pages/Overview";
import InboxPage from "./pages/Inbox";
import NotesPage from "./pages/Notes";
import ChatPage from "./pages/Chat";
import DocumentsPage from "./pages/Documents";
import DocumentDetailPage from "./pages/DocumentDetail";
import ProjectsPage from "./pages/Projects";
import PresentationDetailPage from "./pages/PresentationDetail";
import DatasetsPage from "./pages/Datasets";
import SettingsPage from "./pages/Settings";
import StatsPage from "./pages/Stats";
import HeyGenStudioPage from "./pages/HeyGenStudio";
import BlogPipelinePage from "./pages/BlogPipeline";
import BlogArticleDetailPage from "./pages/BlogArticleDetail";
import BlogV2ArticleDetailPage from "./pages/BlogV2ArticleDetail";
import BlogDashboardPage from "./pages/BlogDashboard";
import BlogTopicDetailPage from "./pages/BlogTopicDetail";
import BlogTrendDetailPage from "./pages/BlogTrendDetail";
import ClaimReviewPage from "./pages/ClaimReview";
import EviClaimReviewPage from "./pages/EviClaimReview";
import EviClaimDeckPage from "./pages/EviClaimDeck";
import ClaimDetailPage from "./pages/ClaimDetail";
import ImageLibraryPage from "./pages/ImageLibrary";
import TasksPage from "./pages/Tasks";
import MailPage from "./pages/Mail";
import CalendarMsPage from "./pages/CalendarMs";
import EmailAutomationPage from "./pages/EmailAutomation";
import TerminPage from "./pages/Termin";
import EqmsPage from "./pages/Eqms";
import FairPage from "./pages/Fair";
import AbstimmungenPage from "./pages/Abstimmungen";
import FragebogenUebersicht from "./questionnaires/Uebersicht";
import FragebogenReview from "./questionnaires/Review";

function RequireAuth({ children }: { children: JSX.Element }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const { pathname } = useLocation();
  // Fair lead capture (CPHI 2026): full-screen mobile app OUTSIDE <Layout>,
  // <RequireAuth> AND <AuthProvider>. It logs in itself via the deep link
  // /fair#k=<token>; AuthProvider would overwrite that token on mount with
  // the (empty) value it read before (auth.tsx: setToken(tok) effect).
  // Its API lives under /api/fair, not /fair (edge prefix whitelist).
  if (pathname === "/fair" || pathname === "/fair/") return <FairPage />;
  // eQMS-Sprachdemo: same pattern as /fair (own login via /eqms#k=<token>, API under /api/eqms-demo).
  if (pathname === "/eqms" || pathname === "/eqms/") return <EqmsPage />;
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Die EINZIGE Route ohne Anmeldung und ohne Atlas-Rahmen. Sie steht
            bewusst hier oben, als Geschwister von /login und NICHT im
            <RequireAuth>-Zweig darunter: der Empfaenger ist ein Kunde oder
            Partner, hat keinen Bearer-Token und wuerde in <Layout> auf einer
            Login-Maske landen. Der Token IM PFAD ist die Autorisierung
            (app/routers/calendar_polls.py) — er gilt fuer genau einen
            Teilnehmer einer Abstimmung, mehr braucht die Seite nicht.
            Pfad /termin (Seite) ≠ /poll (API): die Edge-Kante vor der Prod-URL
            routet /poll ans Backend, ein Browser-Aufruf landete dort sonst beim
            JSON-Endpunkt statt bei dieser Seite. */}
        <Route path="/termin/:token" element={<TerminPage />} />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<OverviewPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          {/* Deep-Link-Fix (2026-06-28): openresty externer Reverse-Proxy
              routet path-basiert; /projects/<id>, /documents/<id> etc clashen
              mit API-Routes und gehen NICHT zum Frontend. Loesung:
              alternative Atlas-spezifische Pfade /p, /docs, /ds, /n, /cfg
              die garantiert nicht in der API existieren. Alte /projects,
              /documents-Routes bleiben in React drin damit interne
              navigate-Calls die noch nicht migriert sind nicht crashen,
              aber externe Deep-Links MUESSEN die neuen /p/<id> usw nutzen. */}
          <Route path="/n" element={<NotesPage />} />
          <Route path="/n/:noteId" element={<NotesPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/notes/:noteId" element={<NotesPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:chatId" element={<ChatPage />} />
          <Route path="/search" element={<Navigate to="/chat" replace />} />
          <Route path="/ingest" element={<Navigate to="/docs" replace />} />
          <Route path="/docs" element={<DocumentsPage />} />
          <Route path="/docs/:documentId" element={<DocumentDetailPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
          <Route path="/p" element={<ProjectsPage />} />
          <Route path="/p/:projectId" element={<ProjectsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectsPage />} />
          {/* /pres/:id (NICHT /presentations/:id) damit kein Clash mit API-Route */}
          <Route
            path="/pres/:presentationId"
            element={<PresentationDetailPage />}
          />
          {/* Konsolidierte Aufgaben-Seite (P2). Alte Einzel-Routen redirecten
              auf die passenden Tabs, damit Deep-Links + Bestands-Links leben. */}
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/mail" element={<MailPage />} />
          <Route path="/email-automation" element={<EmailAutomationPage />} />
          <Route path="/email-automation/p/:prozessId" element={<EmailAutomationPage />} />
          <Route path="/email-automation/p/:prozessId/t/:docTyp" element={<EmailAutomationPage />} />
          <Route path="/email-automation/hebel/:leverId" element={<EmailAutomationPage />} />
          <Route path="/email-automation/roadmap/stufe/:stufeNr" element={<EmailAutomationPage />} />
          <Route path="/email-automation/roadmap/cluster/:clusterId" element={<EmailAutomationPage />} />
          <Route path="/email-automation/decide/:cardId" element={<EmailAutomationPage />} />
          <Route path="/email-automation/a/:ucKey" element={<EmailAutomationPage />} />
          {/* Regeln/Filter (P2.14) — MUSS vor dem Catch-all :useCaseId stehen,
              sonst schluckt der ihn und rendert eine (leere) Katalog-L2. */}
          <Route path="/email-automation/regeln" element={<EmailAutomationPage />} />
          <Route path="/email-automation/:useCaseId" element={<EmailAutomationPage />} />
          <Route path="/kanban" element={<Navigate to="/tasks?view=kanban" replace />} />
          <Route path="/calendar" element={<CalendarMsPage />} />
          {/* Der Stand der EIGENEN Terminabstimmungen. Anmeldepflichtig und im
              Atlas-Rahmen — das genaue Gegenteil von /termin/:token, das ohne
              Anmeldung neben /login haengt.
              Diese Route ist der Grund, warum der Organisator seine Abstimmung
              nach einem Browser-Neustart wiederfindet: der Zustand steckt in
              der URL und im Bearer-Token (localStorage), nicht im Speicher der
              Kalender-Seite. Sie gehoert NICHT in vite.config.ts::API_PATHS —
              dort stuende sie als API-Pfad und lieferte JSON statt der Seite. */}
          <Route path="/abstimmungen" element={<AbstimmungenPage />} />
          <Route path="/abstimmungen/:pollId" element={<AbstimmungenPage />} />
          <Route path="/gantt" element={<Navigate to="/tasks?view=zeitachse" replace />} />
          <Route path="/ds" element={<DatasetsPage />} />
          <Route path="/ds/:datasetId" element={<DatasetsPage />} />
          <Route path="/datasets" element={<DatasetsPage />} />
          <Route path="/datasets/:datasetId" element={<DatasetsPage />} />
          <Route path="/heygen" element={<HeyGenStudioPage />} />
          <Route path="/heygen/:scriptId" element={<HeyGenStudioPage />} />
          <Route path="/blog" element={<BlogPipelinePage />} />
          {/* Die v2-Engine lebt jetzt ganz im Dashboard + Topic-Workspace. Die
              alte Studio-Startseite /blog/v2 leitet aufs Dashboard um; der
              Artikel-Permalink /blog/v2/:articleId löst auf sein Topic auf und
              redirectet in den Workspace (Orphan-Altartikel → Read-only). Beide
              statischen /blog/v2-Pfade stehen VOR der dynamischen v1-Route
              /blog/:articleId — react-router matcht sonst „v2" als :articleId. */}
          <Route path="/blog/v2" element={<Navigate to="/blog/dashboard" replace />} />
          <Route path="/blog/v2/:articleId" element={<BlogV2ArticleDetailPage />} />
          {/* Blog-Cockpit: statische Routen — MÜSSEN vor /blog/:articleId stehen,
              sonst matcht react-router „dashboard"/„topic" als :articleId. */}
          <Route path="/blog/dashboard" element={<BlogDashboardPage />} />
          <Route path="/blog/topic/:id" element={<BlogTopicDetailPage />} />
          <Route path="/blog/trend/:id" element={<BlogTrendDetailPage />} />
          <Route path="/blog/:articleId" element={<BlogArticleDetailPage />} />
          {/* /claims zeigt jetzt die evi_*-Ectoin-Evidenzbasis (Mig 0111);
              die alte Proof-Matrix-Freigabe bleibt unter /claims/legacy
              erreichbar (ImageLibrary/Blog nutzen weiter /api/claims). */}
          {/* /claims/deck = Folien-Präsentation der Claim-Karten; VOR /claims,
              beide statisch (kein Clash). */}
          <Route path="/claims/deck" element={<EviClaimDeckPage />} />
          <Route path="/claims" element={<EviClaimReviewPage />} />
          <Route path="/claims/legacy" element={<ClaimReviewPage />} />
          {/* Per-Claim-Detailseite (in-depth + standalone + Datensatz). Steht NACH
              den statischen /claims/deck|/legacy — die ranken in react-router
              ohnehin höher, aber die Reihenfolge macht es explizit. */}
          <Route path="/claims/:id" element={<ClaimDetailPage />} />
          {/* Fragebögen (Modul „questionnaires"). Die SEITE heisst bewusst
              /fragebogen und nicht /questionnaires: die API liegt unter
              /api/questionnaires, und ein SPA-Pfad, der wie ein API-Präfix
              aussieht, ist genau der Fall, den /p, /docs, /ds oben lösen. */}
          <Route path="/fragebogen" element={<FragebogenUebersicht />} />
          <Route path="/fragebogen/:fragebogenId" element={<FragebogenReview />} />
          <Route path="/library" element={<ImageLibraryPage />} />
          <Route path="/cfg" element={<SettingsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/stats" element={<StatsPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
